import PropTypes from "prop-types";
import React, { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import {
  Grid,
  Tab,
  Tabs,
  useMediaQuery,
  Divider,
  MenuItem,
  List,
  ListItem,
  ListItemText,
} from "@material-ui/core";
import { Autorenew, DeviceHub, VolumeUp, LockOpen } from "@material-ui/icons";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { flatten, isEmpty, noop, range } from "lodash";

import API from "../../../API";
import { withSpinner } from "../../../HOC";
import { withSnackbar } from "../../../HOC/withSnackbar";
import MediumWidget from "../../../components/Layout/MediumWidget";
import { VerticalDivider } from "../../../components/Layout/VerticalDivider";
import OverflowMenu from "../../../components/Navigation/OverflowMenu";
import { FleetContext } from "../../../context";
import { validateSemVar } from "../../../services/text-formatter.service";
import { vehicleType } from "../../../services/types.service";
import DiagnosticModal from "../Modals/DiagnosticModal/DiagnosticModal";
import CommandHistoryModal from "../Modals/CommandHistoryModal";
import buildAssetsUrl from "../../../util/strings/buildAssetsUrl";
import {
  StartRideButton,
  VehicleDataWidgetActionButton,
  VehicleDataWidgetActionButtons,
  VehicleDataWidgetCustomHeader,
  VehicleDataRowSelect,
} from "./subcomponents";
import Permissions from "../../../permissions";
import { PermissionedDisplay } from "../../../components/Layout";
import VehicleSoftwareDetails from "./VehicleSoftwareDetails";
import VehicleHardwareDetails from "./VehicleHardwareDetails";
import VehicleCellularDetails from "./VehicleCellularDetails";
import DiagnosticModalContext from "../Modals/DiagnosticModal/DiagnosticModalContext";
import { useDialog } from "../../../hooks/useDialog";
import { Badge } from "../VehicleHistoryWidget/subcomponents/Badges";
import { useTestIds } from "../../../hooks/useTestIds";
/*
  TODO: Starting / ending trips, changing speed limit, etc are broken on the backend. Test and make sure they still work when they are reenabled.
*/

const useStyles = makeStyles((theme) => ({
  divider: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  tab: {
    margin: `${theme.spacing(0.25)}px 0`,
    minWidth: "33.3%",
  },
  container: {
    width: 340,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
  },
  background: {
    backgroundColor: "#fff",
    backgroundSize: "cover",
    backgroundPosition: "center",
    position: "relative",
    backgroundRepeat: "no-repeat",
    height: 383,
  },
  alertsWrapper: {
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    // Leave room on the right for the overflow menu button, which is
    // absolutely positioned in the top-right corner of this container.
    maxWidth: "70%",
    maxHeight: "100%",
    overflowY: "auto",
  },
  alertPanel: {
    backgroundColor: "rgba(0, 0, 0, 0.65)",
    borderRadius: theme.shape.borderRadius,
    color: "#fff",
    padding: theme.spacing(0.5, 1),
    maxWidth: 260,
  },
  alertPanelHeader: {
    display: "flex",
    alignItems: "center",
    marginLeft: theme.spacing(-1),
  },
  alertCode: {
    fontSize: 12,
    fontWeight: 600,
    color: "#fff",
  },
  alertDescription: {
    fontSize: 11,
    color: "rgba(255, 255, 255, 0.85)",
  },
}));

const VehicleDataWidget = ({ vehicle, setSnackbarMessage, spinner }) => {
  const TABS = [
    { label: "software", path: "software" },
    { label: "hardware", path: "hardware" },
  ];

  const theme = useTheme();
  const classes = useStyles(theme);
  const history = useHistory();
  const location = useLocation();
  const testIds = useTestIds("vehicle-data-widget");
  const { t } = useTranslation();
  const { setActiveVehicle, activeFleet, eventsMap } = useContext(FleetContext);
  const { latestDiagnostics, diagnosticsTimestamp, getDiagnostics } =
    useContext(DiagnosticModalContext);
  const [prevStatus, setPrevStatus] = useState(vehicle.target_state);
  const [isAwaitingSelfCheck, setAwaitingSelfCheck] = useState(false);
  const [diagnosticModalOpen, setDiagnosticModalOpen] = useState(false);
  const [commandHistoryModalOpen, setCommandHistoryModalOpen] = useState(false);

  // this logic is to clear the spinner if the vehicle finishes self-check mode
  useEffect(() => {
    if (prevStatus !== vehicle.target_state) {
      setPrevStatus(vehicle.target_state);
      if (vehicle.target_state !== "self_check") {
        spinner.hide();
      }
    }
  }, [prevStatus, vehicle]);

  const initialTab = () =>
    TABS.filter(({ label }) => location.pathname.toLowerCase().includes(label))[0] ?? TABS[0].label;

  const [currentTab, setCurrentTab] = useState(
    TABS.indexOf(initialTab()) >= 0 ? TABS.indexOf(initialTab()) : 0,
  );

  const openDiagnosticModal = () => {
    setDiagnosticModalOpen(true);
  };

  const handleDiagnosticModalClose = () => () => {
    setDiagnosticModalOpen(false);
  };

  const openCommandHistoryModal = () => {
    setCommandHistoryModalOpen(true);
  };

  const closeCommandHistoryModal = () => () => {
    setCommandHistoryModalOpen(false);
  };

  const ping = async () => {
    spinner.launch();
    await API.vehicle.ping(vehicle.id);
    setTimeout(spinner.hide, 200);
  };

  const selfCheck = () => {
    spinner.launch();
    setAwaitingSelfCheck(true);
    requestSelfCheck();
  };

  const requestSelfCheck = async () => {
    try {
      const activeVehicle = await API.vehicle.setState(vehicle.id, "self_check");
      setAwaitingSelfCheck(false);
      activeVehicle.last_updated = Date.now();
      setActiveVehicle({ ...vehicle, ...activeVehicle });
    } catch (err) {
      spinner.hide();
      setAwaitingSelfCheck(false);
      setSnackbarMessage(err.message);
    }
  };

  const onChangeTab = (_, tab) => {
    setCurrentTab(tab);
  };

  const getBackgroundImage = () => {
    switch (true) {
      case vehicle.type === "vehicle_api.segway":
        return "surf-scooter";
      case vehicle.lock_to_installed && vehicle.seat_installed && vehicle.basket_installed:
        return "scooter-lock-seat-basket";
      case vehicle.lock_to_installed && vehicle.seat_installed:
        return "scooter-lock-seat";
      case vehicle.lock_to_installed && vehicle.basket_installed:
        return "scooter-lock-basket";
      case vehicle.seat_installed && vehicle.basket_installed:
        return "scooter-seat-basket";
      case vehicle.lock_to_installed:
        return "scooter-lock";
      case vehicle.seat_installed:
        return "scooter-seat";
      case vehicle.basket_installed:
        return "scooter-basket";
      default:
        return "scooter-no-lock";
    }
  };

  const requestKeyExchange = async () => {
    try {
      await API.vehicle.keyExchange(vehicle.id);
      setSnackbarMessage("Successfully sent key exchange command.");
    } catch (e) {
      let message = e.message;
      if (e.response) {
        const { status, data } = e.response;
        let description;
        if (Array.isArray(data)) {
          description = data.join("; ");
        } else {
          description = data;
        }
        message = `Error (${status}): ${description}`;
      }
      setSnackbarMessage(message);
    }
  };

  const refreshRadioCert = async () => {
    try {
      await API.vehicle.refreshRadioCert(vehicle.id);
      setSnackbarMessage("Successfully sent refresh radio cert command.");
    } catch (e) {
      let message = e.message;
      if (e.response) {
        const { status, data } = e.response;
        let description;
        if (Array.isArray(data)) {
          description = data.join("; ");
        } else {
          description = data;
        }
        message = `Error (${status}): ${description}`;
      }
      setSnackbarMessage(message);
    }
  };

  const openSwapComponentModal = () => {
    history.push({
      pathname: `${location.pathname}/swap-serialized-component`,
      state: { background: location },
    });
  };
  const openRemoveComponentModal = () => {
    history.push({
      pathname: `${location.pathname}/remove-serialized-component`,
      state: { background: location },
    });
  };

  const resetVehicle = async () => {
    const oldStatus = vehicle.target_state;
    try {
      const data = await API.vehicle.reset(vehicle.id);
      setActiveVehicle({
        ...vehicle,
        ...data,
      });
    } catch (e) {
      setSnackbarMessage(e.message);
      setActiveVehicle({
        ...vehicle,
        target_state: oldStatus,
      });
    }
  };

  const shutdownVehicle = async () => {
    const oldStatus = vehicle.target_state;
    try {
      const data = await API.vehicle.shutdown(vehicle.id);
      setActiveVehicle({
        ...vehicle,
        ...data,
      });
    } catch (e) {
      setSnackbarMessage(e.message);
      setActiveVehicle({
        ...vehicle,
        target_state: oldStatus,
      });
    }
  };

  const patchVehicle = async (data) => {
    try {
      const res = await API.vehicle.patch(vehicle.id, data);
      setSnackbarMessage("Successfully patched vehicle");
      setActiveVehicle({
        ...vehicle,
        ...res,
      });
    } catch (e) {
      setSnackbarMessage(e.message);
    }
  };

  const installLock = async (data) => {
    patchVehicle(data);
    if (data.lock_to_installed) {
      try {
        data.unlock = true;
        const res = await API.vehicle.startTrip(vehicle.id, data);
        setActiveVehicle({
          ...vehicle,
          ...res,
        });
        setSnackbarMessage("Successfully installed lock and started trip");
      } catch (e) {
        setSnackbarMessage(e.message);
      }
    }
  };

  const unlockVehicle = async () => {
    try {
      const { errors, successes } = await API.vehicle.unlock(vehicle.id);
      if (!successes.length) throw new Error("Failed to get response from server");
      if (errors.length > 0) throw new Error("Failed unlocking vehicle");
      setSnackbarMessage("Successfully unlocked vehicle");
    } catch (e) {
      setSnackbarMessage(e.message);
    }
  };

  const unlockBattery = async () => {
    try {
      // TODO -- maybe change to bulk action for this to unlock multiple batteries?
      await API.vehicle.unlockBattery(vehicle.id);
      setSnackbarMessage("Successfully unlocked battery");
    } catch (e) {
      setSnackbarMessage(e.message);
    }
  };

  const vehicleSpeedOptions = range(3, 26); // => [3, ..., 25]

  const updateSpeedLimit = async ({ target: { value: speed } }) => {
    try {
      const data = await API.vehicle.setSpeedLimit(vehicle.id, speed);
      setActiveVehicle(data);
    } catch (error) {
      setSnackbarMessage(t("Vehicles.Detail.Failures.SpeedLimitUpdateFailure"));
    }
  };

  const [openSpeedLimitDialog, speedLimitDialog] = useDialog({
    title: t("Vehicles.Detail.Dialogs.SpeedLimit.Title"),
    body: t("Vehicles.Detail.Dialogs.SpeedLimit.Body"),
    children: (
      <VehicleDataRowSelect value={vehicle.max_speed_limit} onChange={updateSpeedLimit}>
        {vehicleSpeedOptions.map((kph) => (
          <MenuItem key={kph} value={kph}>
            {kph} km/h
          </MenuItem>
        ))}
      </VehicleDataRowSelect>
    ),
  });

  const overflowOptionsDefault = [
    {
      action: openSwapComponentModal,
      label: "SwapSerializedComponent",
      permission: Permissions.basicWrite,
    },
    {
      action: requestKeyExchange,
      label: "RequestKeyExchange",
    },
    {
      action: refreshRadioCert,
      label: "RefreshRadioCert",
    },
    {
      action: resetVehicle,
      label: "Reset",
    },
    {
      action: shutdownVehicle,
      label: "Shutdown",
    },
    {
      action: openCommandHistoryModal,
      label: "ViewCommandHistory",
      permission: Permissions.feature.viewCommandHistory,
    },
    {
      action: () => openSpeedLimitDialog(() => noop),
      label: "SetSpeedLimit",
    },
    {
      action: openRemoveComponentModal,
      label: "RemoveSerializedComponent",
      permission: Permissions.basicWrite,
    },
  ];

  const [overflowOptions, setOverflowOptions] = useState(overflowOptionsDefault);

  useEffect(() => {
    setOverflowOptions([
      {
        action: () => patchVehicle({ seat_installed: !vehicle?.seat_installed }),
        label: vehicle?.seat_installed ? "UninstallSeat" : "InstallSeat",
        permission: null,
      },
      {
        action: () => installLock({ lock_to_installed: !vehicle?.lock_to_installed }),
        label: vehicle?.lock_to_installed ? "UninstallLock" : "InstallLock",
        permission: Permissions.feature.lockTo,
      },
      ...overflowOptionsDefault,
    ]);
  }, [vehicle]);

  const renderFaultHazardAlerts = (faultsOrHazards, type) =>
    !isEmpty(faultsOrHazards) && (
      <div className={classes.alertPanel} data-testid={`${testIds.statusBadge}-${type}`}>
        <div className={classes.alertPanelHeader}>
          <Badge type={type} count={faultsOrHazards.length} />
        </div>
        <List disablePadding dense>
          {faultsOrHazards.map(({ code, description }) => (
            <ListItem key={code} disableGutters>
              <ListItemText
                primary={code}
                secondary={description}
                classes={{
                  primary: classes.alertCode,
                  secondary: classes.alertDescription,
                }}
              />
            </ListItem>
          ))}
        </List>
      </div>
    );

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const vehicleStatus = isAwaitingSelfCheck ? "self_check" : vehicle.target_state;

  const statusesThatDisableSelfCheck = ["reserved", "self_check"];
  const disableSelfCheck = statusesThatDisableSelfCheck.includes(vehicleStatus);

  const dataWidgetButtonDimensions = isMobile ? 20 : 32;
  const latestDiagnostic = latestDiagnostics[0];
  const hasUnlockBattery = vehicle.type === "vehicle_api.segway";

  const buildAlertItems = (entries, idsKey, eventType) =>
    flatten(
      Object.values(entries || {}).map(({ [idsKey]: ids, module }) =>
        Object.values(ids).map((code) => ({
          code: `${module.toUpperCase()}-${code}`,
          description: eventsMap?.[eventType]?.[module]?.[code],
        })),
      ),
    );
  const latchedFaults = buildAlertItems(latestDiagnostic?.latched_faults, "faults", "fault");
  const latchedHazards = buildAlertItems(latestDiagnostic?.latched_hazards, "hazards", "hazard");

  return (
    <MediumWidget
      customHeader={
        <VehicleDataWidgetCustomHeader vehicle={{ ...vehicle, target_state: vehicleStatus }} />
      }
    >
      <Grid container>
        <Grid item md={6} xs={12} className={classes.container}>
          <div
            className={classes.background}
            style={{
              backgroundImage: `url(${buildAssetsUrl(`${getBackgroundImage()}.png`)})`,
            }}
            data-testid={testIds.vehicleImage}
          >
            <div className={classes.alertsWrapper} data-testid={testIds.alertsWrapper}>
              {renderFaultHazardAlerts(latchedFaults, "fault")}
              {renderFaultHazardAlerts(latchedHazards, "hazard")}
            </div>
            <OverflowMenu options={overflowOptions} />
            <StartRideButton vehicle={vehicle} />
          </div>
          <VehicleDataWidgetActionButtons>
            <VehicleDataWidgetActionButton
              id="ping-button"
              action="Ping"
              icon={
                <VolumeUp
                  style={{
                    height: dataWidgetButtonDimensions,
                    width: dataWidgetButtonDimensions,
                  }}
                />
              }
              handleClick={ping}
            />
            {!isMobile && <VerticalDivider />}
            <VehicleDataWidgetActionButton
              id="self-check-button"
              action="SelfCheck"
              icon={
                <Autorenew
                  style={{
                    height: dataWidgetButtonDimensions,
                    width: dataWidgetButtonDimensions,
                  }}
                />
              }
              handleClick={selfCheck}
              disabled={disableSelfCheck}
            />
            {!isMobile && <VerticalDivider />}
            {hasUnlockBattery && (
              <VehicleDataWidgetActionButton
                id="unlock-battery"
                action="UnlockBattery"
                icon={
                  <LockOpen
                    style={{
                      height: dataWidgetButtonDimensions,
                      width: dataWidgetButtonDimensions,
                    }}
                  />
                }
                handleClick={unlockBattery}
              />
            )}
            {!hasUnlockBattery && (
              <PermissionedDisplay permission={Permissions.feature.lockTo}>
                <VehicleDataWidgetActionButton
                  id="unlock-button"
                  action="Unlock"
                  icon={
                    <LockOpen
                      style={{
                        height: dataWidgetButtonDimensions,
                        width: dataWidgetButtonDimensions,
                      }}
                    />
                  }
                  handleClick={unlockVehicle}
                  disabled={!vehicle?.lock_to_installed || !activeFleet?.lock_to_enabled}
                />
                {!isMobile && <VerticalDivider />}
              </PermissionedDisplay>
            )}
            <VehicleDataWidgetActionButton
              id="diagnostic-modal-button"
              action="Diagnostics"
              icon={
                <DeviceHub
                  style={{
                    height: dataWidgetButtonDimensions,
                    width: dataWidgetButtonDimensions,
                  }}
                />
              }
              handleClick={openDiagnosticModal}
            />
          </VehicleDataWidgetActionButtons>
        </Grid>
        <Grid item md={6} xs={12} className={classes.noPadding}>
          <Tabs value={currentTab} onChange={onChangeTab} indicatorColor="secondary" centered>
            <Tab label={t("Vehicles.ConfigProfile.Software")} className={classes.tab} />
            <Tab label={t("Vehicles.ConfigProfile.Hardware")} className={classes.tab} />
            <Tab label={t("Vehicles.ConfigProfile.SIM")} className={classes.tab} />
          </Tabs>
          <Divider />
          {currentTab === 0 && (
            <VehicleSoftwareDetails vehicle={vehicle} setSnackbarMessage={setSnackbarMessage} />
          )}
          {currentTab === 1 && <VehicleHardwareDetails vehicle={vehicle} />}
          {currentTab === 2 && <VehicleCellularDetails vehicle={vehicle} />}
        </Grid>
      </Grid>
      <DiagnosticModal
        modalOpen={diagnosticModalOpen}
        handleModalClose={handleDiagnosticModalClose}
        sortedDiagnostics={latestDiagnostics}
        diagnosticsTimestamp={diagnosticsTimestamp}
        refreshFunction={() => getDiagnostics(vehicle.id)}
      />
      <CommandHistoryModal
        modalOpen={commandHistoryModalOpen}
        handleModalClose={closeCommandHistoryModal}
        vehicleId={vehicle.id}
      />
      {spinner.component}
      {speedLimitDialog}
    </MediumWidget>
  );
};

const validateVersion = (version, defaultVersion) =>
  validateSemVar(version) ? version : defaultVersion;

VehicleDataWidget.propTypes = {
  vehicle: vehicleType.isRequired,
  setSnackbarMessage: PropTypes.func.isRequired,
  spinner: PropTypes.object.isRequired,
};

export default withSpinner(withSnackbar(VehicleDataWidget));
export { VehicleDataWidget, validateVersion };
